/**
 * @file auth.store.ts
 * @description Zustand store for the SPA's authentication state. Mirrors
 *   the in-memory access token from `lib/api` and exposes decoded JWT
 *   claims (role, sub, locale) for UX-only checks. A `bootstrapped` flag
 *   guards routes until the initial silent refresh completes.
 * @module Stores
 * @security The store holds ONLY the access token in memory — never
 *   persisted to localStorage. Role decoded from JWT is for UX only;
 *   real authorization happens on the API.
 */

import { create } from 'zustand';
import { getAccessToken, onSessionExpired, onTokenChange, setAccessToken } from '../lib/api';
import { type DecodedAccessToken, decodeJwtPayload } from '../lib/jwt-decode';

interface AuthState {
  accessToken: string | null;
  claims: DecodedAccessToken | null;
  bootstrapped: boolean;
  /**
   * True only after a refresh fails MID-SESSION. Flipped by the
   * onSessionExpired signal from lib/api. LoginPage reads it to show
   * a "your session ended" banner; cleared on the next successful
   * setAccessToken (login or refresh) so an explicit re-login resets
   * the UI.
   */
  sessionExpired: boolean;
  setBootstrapped: (v: boolean) => void;
  clearSessionExpired: () => void;
  clear: () => void;
}

function deriveClaims(token: string | null): DecodedAccessToken | null {
  return token === null ? null : decodeJwtPayload(token);
}

export const useAuthStore = create<AuthState>((set) => {
  // Mirror api.ts token into the store so components re-render on change.
  onTokenChange((token) =>
    set((prev) => ({
      accessToken: token,
      claims: deriveClaims(token),
      // A fresh token always clears the expired banner.
      sessionExpired: token === null ? prev.sessionExpired : false,
    })),
  );
  onSessionExpired(() => set({ sessionExpired: true }));
  const initial = getAccessToken();
  return {
    accessToken: initial,
    claims: deriveClaims(initial),
    bootstrapped: false,
    sessionExpired: false,
    setBootstrapped: (v) => set({ bootstrapped: v }),
    clearSessionExpired: () => set({ sessionExpired: false }),
    clear: () => {
      setAccessToken(null);
      set({ accessToken: null, claims: null });
    },
  };
});

export const useIsAuthenticated = (): boolean =>
  useAuthStore((state) => state.accessToken !== null);

export const useIsAdmin = (): boolean => useAuthStore((state) => state.claims?.role === 'admin');

export const useSessionExpired = (): boolean => useAuthStore((state) => state.sessionExpired);
